// js/tradeControlUI.js
// Gestión de UI y modos del Trade Control Panel

const TC_MODES = {
    PRE_TRADE: 'pre-trade',
    AI_ANALYSIS: 'ai-analysis',
    IN_SESSION: 'in-session'
};

class TradeControlUI {
    constructor() {
        this.currentMode = TC_MODES.PRE_TRADE;
        this.panel = null;
        this.overlay = null;
        this.sessionPanel = null;
        this.checklistState = {};
        this.isOpen = false;
        this.refreshInterval = null;
    }

    /**
     * Inicializa la UI del Trade Control
     */
    init() {
        this.createMainPanel();
        this.loadChecklistState();
        this.setupEventListeners();
    }

    /**
     * Crea el panel principal (overlay + contenedor)
     */
    createMainPanel() {
        // Overlay
        this.overlay = document.createElement('div');
        this.overlay.id = 'tradeControlOverlay';
        this.overlay.className = 'trade-control-overlay';
        this.overlay.addEventListener('click', () => this.close());

        // Panel principal
        this.panel = document.createElement('div');
        this.panel.id = 'tradeControlPanel';
        this.panel.className = 'trade-control-panel';
        this.panel.addEventListener('click', (e) => e.stopPropagation());

        document.body.appendChild(this.overlay);
        document.body.appendChild(this.panel);
    }

    /**
     * Abre el panel en un modo específico
     */
    open(mode = TC_MODES.PRE_TRADE) {
        if (this.isOpen) return;

        this.currentMode = mode;
        this.isOpen = true;

        this.overlay.classList.add('show');
        this.panel.classList.add('open');

        this.render();
    }

    /**
     * Cierra el panel principal
     */
    close() {
        if (!this.isOpen) return;

        this.overlay.classList.remove('show');
        this.panel.classList.remove('open');

        // Detener auto-refresh si está activo
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }

        setTimeout(() => {
            this.isOpen = false;
        }, 300);
    }

    /**
     * Cambia entre modos
     */
    switchMode(mode) {
        this.currentMode = mode;
        
        // Detener refresh anterior
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
        
        // Iniciar auto-refresh si es modo IN-SESSION
        if (mode === TC_MODES.IN_SESSION) {
            this.startAutoRefresh();
        }
        
        this.render();
    }

    /**
     * Renderiza el contenido según el modo actual
     */
    render() {
        switch (this.currentMode) {
            case TC_MODES.AI_ANALYSIS:
                this.renderAIAnalysisMode();
                break;
            case TC_MODES.IN_SESSION:
                this.renderInSessionMode();
                break;
            case TC_MODES.PRE_TRADE:
            default:
                this.renderPreTradeMode();
                break;
        }
    }

    /**
     * MODO: PRE-TRADE
     */
    renderPreTradeMode() {
        const score = window.tradeControl?.getLatestScore() || { score: 0, status: 'CAUTION', message: 'Calculando...' };
        const trades = window.tradeControl?.getTodayTradeCount() || 0;
        const target = window.tradeControl?.getDailyTarget() || 200;
        const maxTrades = window.tradeControl?.getMaxTrades() || 3;
        const canTrade = this.canStartTrading();

        this.panel.innerHTML = `
            <div class="tc-header">
                <div class="tc-header-left">
                    <div class="tc-logo">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div>
                        <h2 class="tc-title">Trade Control</h2>
                        <p class="tc-subtitle">Pre-Trade Analysis</p>
                    </div>
                </div>
                <button class="tc-close-btn" onclick="window.tradeControlUI.close()">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <div class="tc-content">
                <!-- Configuración de Sesión -->
                <div class="tc-section">
                    <h3 class="tc-section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 6px;">
                            <circle cx="12" cy="12" r="3" stroke-width="2"/>
                            <path d="M12 1v6m0 6v6M1 12h6m6 0h6" stroke-width="2" stroke-linecap="round"/>
                            <path d="M4.2 4.2l4.3 4.3m7 7l4.3 4.3m0-15.6l-4.3 4.3m-7 7l-4.3 4.3" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        Configuración de Sesión
                    </h3>
                    <div class="session-config">
                        <div class="config-row">
                            <label for="tc_target">Objetivo Diario ($)</label>
                            <input 
                                type="number" 
                                id="tc_target" 
                                value="${target}" 
                                step="50"
                                min="0"
                                onchange="window.tradeControl.setDailyTarget(parseFloat(this.value))"
                            >
                        </div>
                        <div class="config-row">
                            <label for="tc_max_trades">Máximo Trades</label>
                            <input 
                                type="number" 
                                id="tc_max_trades" 
                                value="${maxTrades}" 
                                step="1"
                                min="1"
                                max="10"
                                onchange="window.tradeControl.setMaxTrades(parseInt(this.value)); window.tradeControlUI.render();"
                            >
                        </div>
                    </div>
                </div>

                <!-- Botón de Análisis IA -->
                <div class="tc-section">
                    <button class="tc-ai-btn" onclick="window.tradeControlUI.switchMode('ai-analysis'); window.tradeControl.runAIAnalysis();">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <span>🚀 Analizar Condiciones con IA</span>
                    </button>
                </div>

                <!-- Pre-Flight Checklist -->
                <div class="tc-section">
                    <h3 class="tc-section-title">Pre-Flight Checklist</h3>
                    <div class="tc-checklist">
                        ${this.renderChecklistItems(trades, maxTrades)}
                    </div>
                </div>

                <!-- Botón Ready to Trade -->
                <div class="tc-section">
                    <button 
                        class="tc-ready-btn ${canTrade ? '' : 'disabled'}" 
                        ${canTrade ? '' : 'disabled'}
                        onclick="${canTrade ? 'window.tradeControlUI.startTrading()' : ''}"
                    >
                        ${canTrade ? `
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 8px;">
                            <path d="M20 6L9 17l-5-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <span>READY TO TRADE</span>` : `
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 8px;">
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke-width="2"/>
                            <path d="M12 11V7a3 3 0 116 0v4" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        <span>Completa el checklist para habilitar</span>`}
                    </button>
                    ${!canTrade ? '<p class="tc-hint">Marca todos los items requeridos para continuar</p>' : ''}
                </div>
            </div>
        `;
    }

    /**
     * MODO: AI ANALYSIS
     */
    async renderAIAnalysisMode() {
        this.panel.innerHTML = `
            <div class="tc-header">
                <button class="tc-back-btn" onclick="window.tradeControlUI.switchMode('pre-trade')">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                <div>
                    <h2 class="tc-title">AI Analysis</h2>
                    <p class="tc-subtitle">Análisis Predictivo</p>
                </div>
                <button class="tc-close-btn" onclick="window.tradeControlUI.close()">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <div class="tc-content tc-ai-content">
                <div class="tc-loading">
                    <div class="tc-spinner"></div>
                    <p>Analizando tu historial de trading...</p>
                </div>
            </div>
        `;

        // Esperar análisis con timeout
        setTimeout(async () => {
            try {
                const analysisPromise = window.tradeControl.getAIAnalysis();
                const timeoutPromise = new Promise((resolve) => 
                    setTimeout(() => resolve(null), 10000) // 10 segundos máximo
                );
                
                const analysis = await Promise.race([analysisPromise, timeoutPromise]);
                
                if (analysis) {
                    this.renderAIResults(analysis);
                } else {
                    // Timeout - mostrar mensaje de error
                    const content = this.panel.querySelector('.tc-ai-content');
                    content.innerHTML = `
                        <div class="tc-error">
                            <p>⚠️ No se pudo completar el análisis</p>
                            <p style="font-size: 14px; opacity: 0.7;">Intenta de nuevo en unos momentos</p>
                            <button onclick="window.tradeControlUI.switchMode('AI_ANALYSIS')" class="tc-btn-primary" style="margin-top: 20px;">
                                Reintentar
                            </button>
                        </div>
                    `;
                }
            } catch (error) {

                const content = this.panel.querySelector('.tc-ai-content');
                content.innerHTML = `
                    <div class="tc-error">
                        <p>⚠️ Error al cargar el análisis</p>
                        <button onclick="window.tradeControlUI.switchMode('AI_ANALYSIS')" class="tc-btn-primary" style="margin-top: 20px;">
                            Reintentar
                        </button>
                    </div>
                `;
            }
        }, 2000);
    }

    /**
     * Renderiza resultados del análisis IA
     */
    renderAIResults(analysis) {
        const content = this.panel.querySelector('.tc-ai-content');
        
        content.innerHTML = `
            <!-- Recomendación Global -->
            <div class="tc-section">
                <div class="ai-recommendation ${analysis.recommendation.toLowerCase()}">
                    <div class="ai-rec-icon">${this.getRecommendationIcon(analysis.recommendation)}</div>
                    <div class="ai-rec-content">
                        <h3>${analysis.message}</h3>
                        <div class="ai-confidence">
                            <span>Confianza: ${analysis.confidence}%</span>
                            <div class="confidence-bar">
                                <div class="confidence-fill" style="width: ${analysis.confidence}%"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Análisis de Pares -->
            <div class="tc-section">
                <h3 class="tc-section-title">📈 Análisis de Pares</h3>
                <div class="ai-pairs-grid">
                    <div class="ai-pair-card best">
                        <div class="pair-badge">⭐ Mejor Par</div>
                        <h4>${analysis.bestPair.name}</h4>
                        <div class="pair-stats">
                            <div class="pair-stat">
                                <span class="stat-label">Win Rate</span>
                                <span class="stat-value">${analysis.bestPair.winRate}%</span>
                            </div>
                            <div class="pair-stat">
                                <span class="stat-label">Avg Profit</span>
                                <span class="stat-value">$${analysis.bestPair.avgProfit}</span>
                            </div>
                            <div class="pair-stat">
                                <span class="stat-label">Trades</span>
                                <span class="stat-value">${analysis.bestPair.trades}</span>
                            </div>
                        </div>
                        <p class="pair-reason">${analysis.bestPair.reason}</p>
                    </div>

                    <div class="ai-pair-card worst">
                        <div class="pair-badge">⚠️ Evitar</div>
                        <h4>${analysis.worstPair.name}</h4>
                        <div class="pair-stats">
                            <div class="pair-stat">
                                <span class="stat-label">Win Rate</span>
                                <span class="stat-value">${analysis.worstPair.winRate}%</span>
                            </div>
                            <div class="pair-stat">
                                <span class="stat-label">Avg Loss</span>
                                <span class="stat-value">$${analysis.worstPair.avgLoss}</span>
                            </div>
                        </div>
                        <p class="pair-reason">${analysis.worstPair.reason}</p>
                    </div>
                </div>
            </div>

            <!-- Análisis del Día -->
            <div class="tc-section">
                <h3 class="tc-section-title">📅 Análisis del ${analysis.dayAnalysis.dayOfWeek}</h3>
                <div class="day-analysis-card ${analysis.dayAnalysis.status}">
                    <div class="day-wr-display">
                        <div class="day-wr-main">${analysis.dayAnalysis.historicalWR}%</div>
                        <div class="day-wr-label">Win Rate Histórico</div>
                    </div>
                    <div class="day-comparison">
                        <div class="comparison-item">
                            <span>Promedio General:</span>
                            <strong>${analysis.dayAnalysis.avgWR}%</strong>
                        </div>
                        <div class="comparison-item">
                            <span>Diferencia:</span>
                            <strong class="${analysis.dayAnalysis.historicalWR >= analysis.dayAnalysis.avgWR ? 'positive' : 'negative'}">
                                ${analysis.dayAnalysis.historicalWR - analysis.dayAnalysis.avgWR > 0 ? '+' : ''}${(analysis.dayAnalysis.historicalWR - analysis.dayAnalysis.avgWR).toFixed(1)}%
                            </strong>
                        </div>
                        <div class="comparison-item">
                            <span>Trades Analizados:</span>
                            <strong>${analysis.dayAnalysis.tradesAnalyzed}</strong>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Análisis de Horario -->
            <div class="tc-section">
                <h3 class="tc-section-title">⏰ Análisis de Horario</h3>
                <div class="time-analysis-card">
                    <div class="time-optimal ${analysis.timeAnalysis.isOptimal ? 'active' : ''}">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                            <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        <div>
                            <div class="time-label">Mejor Horario</div>
                            <div class="time-value">${analysis.timeAnalysis.bestHour}</div>
                            <div class="time-wr">Win Rate: ${analysis.timeAnalysis.hourWR}%</div>
                        </div>
                    </div>
                    <div class="time-current">
                        <span>Hora Actual:</span>
                        <strong>${analysis.timeAnalysis.currentTime}</strong>
                        ${analysis.timeAnalysis.isOptimal ? 
                            '<span class="time-badge optimal"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 4px;"><path d="M20 6L9 17l-5-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Horario Óptimo</span>' : 
                            '<span class="time-badge suboptimal">⚠️ Fuera de horario óptimo</span>'
                        }
                    </div>
                </div>
            </div>

            <!-- Análisis de Racha -->
            <div class="tc-section">
                <h3 class="tc-section-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 6px;">
                        <circle cx="12" cy="12" r="10" stroke-width="2"/>
                        <circle cx="12" cy="12" r="6" stroke-width="2"/>
                        <circle cx="12" cy="12" r="2" fill="currentColor"/>
                    </svg>
                    Análisis de Racha
                </h3>
                <div class="streak-analysis-card">
                    <div class="streak-header">
                        <div class="streak-icon ${analysis.streakAnalysis.current.toLowerCase()}">${analysis.streakAnalysis.current === 'WIN' ? '🔥' : '❄️'}</div>
                        <div class="streak-info">
                            <div class="streak-label">Racha Actual</div>
                            <div class="streak-count">${analysis.streakAnalysis.consecutive} ${analysis.streakAnalysis.current === 'WIN' ? 'Victorias' : 'Pérdidas'}</div>
                        </div>
                    </div>
                    <div class="streak-pattern">
                        <p><strong>Patrón Detectado:</strong></p>
                        <p>${analysis.streakAnalysis.pattern}</p>
                    </div>
                    <div class="streak-recommendation">
                        <strong>Recomendación:</strong> ${analysis.streakAnalysis.recommendation}
                    </div>
                </div>
            </div>

            <!-- Key Insights -->
            <div class="tc-section">
                <h3 class="tc-section-title">💡 Key Insights</h3>
                <div class="insights-list">
                    ${analysis.keyInsights.map(insight => `
                        <div class="insight-item">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <p>${insight}</p>
                        </div>
                    `).join('')}
                </div>
            </div>

            <!-- Botón para volver -->
            <div class="tc-section">
                <button class="tc-secondary-btn" onclick="window.tradeControlUI.switchMode('pre-trade')">
                    ← Volver a Pre-Trade
                </button>
            </div>
        `;
    }

    /**
     * MODO: IN-SESSION
     */
    renderInSessionMode() {
        // Cerrar panel principal
        this.close();

        // Crear panel flotante compacto
        if (!this.sessionPanel) {
            this.sessionPanel = document.createElement('div');
            this.sessionPanel.id = 'sessionPanel';
            this.sessionPanel.className = 'session-panel';
            document.body.appendChild(this.sessionPanel);
            
            // Hacer el panel draggable
            this.makePanelDraggable(this.sessionPanel);
        }

        // Centrar al abrir si no hay posición guardada
        const savedPosition = localStorage.getItem('sessionPanelPosition');
        if (!savedPosition) {
            this.sessionPanel.style.left = '50%';
            this.sessionPanel.style.top = '50%';
            this.sessionPanel.style.right = 'auto';
            this.sessionPanel.style.bottom = 'auto';
            this.sessionPanel.style.transform = 'translate(-50%, -50%)';
        }

        this.updateSessionPanel();
        this.sessionPanel.style.display = 'block';
        // Pequeño delay para que la transición funcione
        setTimeout(() => {
            this.sessionPanel.classList.add('show');
        }, 10);

        // Auto-update cada 30 segundos
        if (this.sessionUpdateInterval) clearInterval(this.sessionUpdateInterval);
        this.sessionUpdateInterval = setInterval(() => {
            this.updateSessionPanel();
        }, 30000);

        // Notificación warning al abrir la sesión
        const infoMessage = 'Mientras esta pestaña esté abierta se gestionará automáticamente el nº de trades máximo, pérdida máxima y target del plan.';
        if (window.showAlert) {
            window.showAlert('Sesión activa', infoMessage, 'warning');
        } else if (window.showNotification) {
            window.showNotification(infoMessage, 'warning', 5000);
        } else if (this.showToast) {
            this.showToast(infoMessage, 'warning');
        }
    }
    
    /**
     * Hace que el panel sea draggable y guarda su posición
     */
    makePanelDraggable(panel) {
        let isDragging = false;
        let currentX;
        let currentY;
        let initialX;
        let initialY;
        let xOffset = 0;
        let yOffset = 0;

        // Cargar posición guardada
        const savedPosition = localStorage.getItem('sessionPanelPosition');
        if (savedPosition) {
            const { x, y } = JSON.parse(savedPosition);
            panel.style.left = `${x}px`;
            panel.style.top = `${y}px`;
            panel.style.right = 'auto';
            panel.style.bottom = 'auto';
            panel.style.transform = 'none';
            xOffset = x;
            yOffset = y;
        }

        panel.addEventListener('mousedown', dragStart);
        document.addEventListener('mousemove', drag);
        document.addEventListener('mouseup', dragEnd);
        
        // Touch events para móvil
        panel.addEventListener('touchstart', dragStart, { passive: false });
        document.addEventListener('touchmove', drag, { passive: false });
        document.addEventListener('touchend', dragEnd);

        function dragStart(e) {
            // Solo arrastrar si se hace clic en el header
            const target = e.target;
            if (!target.closest('.session-header') || target.closest('button')) {
                return;
            }

            // Anclar la posición actual antes del primer drag para evitar salto
            if (!panel.style.left || !panel.style.top || panel.style.left === '' || panel.style.top === '') {
                const rect = panel.getBoundingClientRect();
                xOffset = rect.left;
                yOffset = rect.top;
                panel.style.left = `${xOffset}px`;
                panel.style.top = `${yOffset}px`;
                panel.style.right = 'auto';
                panel.style.bottom = 'auto';
                panel.style.transform = 'none';
            }
            
            panel.style.cursor = 'grabbing';
            
            if (e.type === 'touchstart') {
                initialX = e.touches[0].clientX - xOffset;
                initialY = e.touches[0].clientY - yOffset;
            } else {
                initialX = e.clientX - xOffset;
                initialY = e.clientY - yOffset;
            }

            isDragging = true;
        }

        function drag(e) {
            if (isDragging) {
                e.preventDefault();

                if (e.type === 'touchmove') {
                    currentX = e.touches[0].clientX - initialX;
                    currentY = e.touches[0].clientY - initialY;
                } else {
                    currentX = e.clientX - initialX;
                    currentY = e.clientY - initialY;
                }

                xOffset = currentX;
                yOffset = currentY;

                // Limitar al viewport
                const rect = panel.getBoundingClientRect();
                const maxX = window.innerWidth - rect.width;
                const maxY = window.innerHeight - rect.height;
                
                xOffset = Math.max(0, Math.min(xOffset, maxX));
                yOffset = Math.max(0, Math.min(yOffset, maxY));

                panel.style.left = `${xOffset}px`;
                panel.style.top = `${yOffset}px`;
                panel.style.right = 'auto';
                panel.style.bottom = 'auto';
            }
        }

        function dragEnd() {
            if (isDragging) {
                initialX = currentX;
                initialY = currentY;
                isDragging = false;
                panel.style.cursor = 'grab';
                
                // Guardar posición
                localStorage.setItem('sessionPanelPosition', JSON.stringify({
                    x: xOffset,
                    y: yOffset
                }));
            }
        }
    }

    /**
     * Actualiza el panel de sesión
     */
    async updateSessionPanel() {
        const trades = await window.tradeControl.getTodayTradeCount();
        const pnl = await window.tradeControl.getTodayPnL();
        const target = await window.tradeControl.getDailyTarget(); // 🆕 Ahora es async
        const maxLoss = await window.tradeControl.getMaxDailyLoss(); // 🆕 Ahora es async y obtiene del plan
        const maxTrades = await window.tradeControl.getMaxTradesAsync();
        
        // 🆕 Corregir cálculo de progreso: nunca negativo, máximo 100%
        const progress = pnl >= 0 ? Math.min((pnl / target) * 100, 100) : 0;
        
        // 🆕 LÓGICA CORRECTA: 
        // Si PnL positivo → va a objetivo, pérdidas = 0
        // Si PnL negativo → objetivo = 0, pérdidas = valor absoluto
        let displayPnL, currentLoss;
        
        if (pnl >= 0) {
            // Día positivo: mostrar ganancia en objetivo
            displayPnL = pnl;
            currentLoss = 0;
        } else {
            // Día negativo: objetivo = 0, pérdidas = valor absoluto  
            displayPnL = 0;
            currentLoss = Math.abs(pnl);
        }
        
        const lossProgress = Math.min((currentLoss / maxLoss) * 100, 100);

        this.sessionPanel.innerHTML = `
            <div class="session-header">
                <h3>Session Active</h3>
                <div style="display: flex; gap: 8px;">
                    <button onclick="window.tradeControlUI.refreshSession()" title="Actualizar datos">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button onclick="window.tradeControlUI.openChecklistDaily()" title="Configuración">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="3" stroke-width="2"/>
                            <path d="M12 1v6m0 6v6M1 12h6m6 0h6" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                    <button onclick="window.tradeControlUI.closeSession()" title="Cerrar sesión">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
            </div>
            
            <div class="session-stats">
                <div class="stat-box">
                    <span class="stat-label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
                            <circle cx="12" cy="12" r="10" stroke-width="2"/>
                            <circle cx="12" cy="12" r="2" fill="currentColor"/>
                        </svg>
                        Trades
                    </span>
                    <span class="stat-value">${trades}/${maxTrades}</span>
                </div>
                
                <div class="stat-box progress-box">
                    <div class="progress-label">
                        <span>Objetivo</span>
                        <span>$${displayPnL.toFixed(2)}/$${target}</span>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-fill" style="width: ${progress}%"></div>
                    </div>
                </div>
                
                <div class="stat-box progress-box">
                    <div class="progress-label">
                        <span>Pérdida Máx</span>
                        <span>$${currentLoss.toFixed(2)}/-$${maxLoss}</span>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-fill loss" style="width: ${lossProgress}%"></div>
                    </div>
                </div>
            </div>
        `;
        
        // Efecto de confeti si se alcanza el objetivo
        if (progress >= 100 && !this.confettiTriggered) {
            this.confettiTriggered = true;
            this.triggerCelebration();
        } else if (progress < 100) {
            this.confettiTriggered = false;
        }
    }
    
    /**
     * Efecto de celebración (confeti)
     */
    triggerCelebration() {
        // Crear confeti
        const confetti = document.createElement('div');
        confetti.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 99999;
        `;
        
        confetti.innerHTML = `
            <div style="position: absolute; width: 100%; height: 100%;">
                ${Array(50).fill(0).map((_, i) => {
                    const left = Math.random() * 100;
                    const delay = Math.random() * 0.5;
                    const duration = 2 + Math.random() * 1;
                    const size = 5 + Math.random() * 10;
                    const color = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#ec4899'][Math.floor(Math.random() * 5)];
                    
                    return `
                        <div style="
                            position: absolute;
                            left: ${left}%;
                            top: -10px;
                            width: ${size}px;
                            height: ${size}px;
                            background: ${color};
                            border-radius: 50%;
                            animation: fall ${duration}s ease-in ${delay}s forwards;
                        "></div>
                    `;
                }).join('')}
            </div>
            <style>
                @keyframes fall {
                    to {
                        transform: translateY(100vh) rotate(360deg);
                        opacity: 0;
                    }
                }
            </style>
        `;
        
        document.body.appendChild(confetti);
        
        // Mostrar alerta de felicidades
        if (window.showAlert) {
            window.showAlert('🎉 ¡Felicidades!', '¡Has alcanzado tu objetivo diario!', 'success');
        }
        
        // Eliminar confeti después de 3 segundos
        setTimeout(() => confetti.remove(), 3000);
    }

    /**
     * Renderiza los items del checklist
     */
    renderChecklistItems(trades, maxTrades) {
        const items = [
            {
                id: 'news_reviewed',
                label: 'He revisado las noticias económicas de hoy',
                required: true
            },
            {
                id: 'emotional_state',
                label: 'Mi estado emocional es calm/focused',
                required: true
            },
            {
                id: 'optimal_time',
                label: 'Estoy dentro de mi horario óptimo',
                required: true
            },
            {
                id: 'trade_limit',
                label: `No he alcanzado mi límite de trades`,
                required: true,
                disabled: trades >= maxTrades
            },
            {
                id: 'setup_valid',
                label: 'Mi setup cumple con mi plan de trading',
                required: true
            }
        ];

        return items.map(item => {
            const isChecked = this.checklistState[item.id] || false;
            return `
                <div class="checklist-item ${item.disabled ? 'disabled' : ''}">
                    <label class="checkbox-wrapper">
                        <input 
                            type="checkbox" 
                            id="check_${item.id}"
                            ${isChecked ? 'checked' : ''}
                            ${item.disabled ? 'disabled' : ''}
                            onchange="window.tradeControlUI.toggleChecklistItem('${item.id}')"
                        >
                        <span class="checkbox-custom"></span>
                        <span class="checkbox-label">${item.label}</span>
                    </label>
                    ${item.required ? '<span class="required-badge">Requerido</span>' : ''}
                </div>
            `;
        }).join('');
    }

    /**
     * Toggle de item del checklist
     */
    toggleChecklistItem(itemId) {
        this.checklistState[itemId] = !this.checklistState[itemId];
        this.saveChecklistState();
        this.render(); // Re-render para actualizar botón
    }

    /**
     * Verifica si puede empezar a tradear
     */
    canStartTrading() {
        const requiredItems = ['news_reviewed', 'emotional_state', 'optimal_time', 'trade_limit', 'setup_valid'];
        return requiredItems.every(id => this.checklistState[id] === true);
    }

    /**
     * Inicia sesión de trading
     */
    async startTrading() {
        if (!this.canStartTrading()) {
            showError('Debes completar todos los items del checklist antes de empezar.');
            return;
        }

        try {
            await window.tradeControl.activateAutoManagementSession();
        } catch (error) {
            this.showToast('No se pudo activar la sesión en MT. Revisa la cuenta sincronizada.', 'error');
            return;
        }

        if (window.planManagerV2 && window.currentAccount) {
            window.planManagerV2.setPlanState(window.currentAccount, 'active');
            window.planManagerV2.updateButtonLabel();
        }

        // Cambiar a modo sesión
        this.switchMode(TC_MODES.IN_SESSION);
        
        // Toast de confirmación
        this.showToast('Sesión iniciada - Buena suerte!', 'success');
    }

    /**
     * Abre el checklist diario - wrapper para onclick
     */
    openChecklistDaily() {
        try {
            if (!window.planManagerV2) {
                return;
            }
            
            // Cerrar sessionPanel
            if (this.sessionPanel) {
                this.sessionPanel.classList.remove('show');
                setTimeout(() => {
                    if (this.sessionPanel) this.sessionPanel.style.display = 'none';
                }, 300);
            }
            
            if (this.sessionUpdateInterval) {
                clearInterval(this.sessionUpdateInterval);
            }

            if (window.planManagerV2 && window.currentAccount) {
                window.planManagerV2.setPlanState(window.currentAccount, 'created');
                window.planManagerV2.updateButtonLabel();
            }
            
            // Llamar al método - puede ser sync o async
            const result = window.planManagerV2.openFollowPlanModal();
            
            // Si es Promise, manejo de errores
            if (result && typeof result.catch === 'function') {
                result.catch(error => {
                    window.showAlert('Error al cargar el checklist', 'error');
                });
            }
        } catch (error) {
            window.showAlert('Error al abrir checklist', 'error');
        }
    }

    /**
     * Abre el checklist diario del plan
     */
    backToPreTrade() {
        // Cerrar sessionPanel
        if (this.sessionPanel) {
            this.sessionPanel.classList.remove('show');
            setTimeout(() => {
                if (this.sessionPanel) this.sessionPanel.style.display = 'none';
            }, 300);
        }
        if (this.sessionUpdateInterval) {
            clearInterval(this.sessionUpdateInterval);
        }

        if (window.planManagerV2 && window.currentAccount) {
            window.planManagerV2.setPlanState(window.currentAccount, 'created');
            window.planManagerV2.updateButtonLabel();
        }
        
        // Abrir checklist diario del plan
        if (window.planManagerV2 && window.planManagerV2.openFollowPlanModal) {
            const result = window.planManagerV2.openFollowPlanModal();
            // Si retorna una Promise (async), esperar a que se cargue
            if (result && typeof result.catch === 'function') {
                result.catch(error => {
                });
            }
        } else {
        }
    }

    /**
     * Evalúa un nuevo trade desde el modo sesión
     */
    evaluateNewTrade() {
        this.backToPreTrade();
        this.showToast('Evaluando nuevo trade...', 'info');
    }

    /**
     * Helpers
     */
    getStatusIcon(status) {
        switch (status) {
            case 'SAFE': return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" style="display: inline-block; vertical-align: middle;"><path d="M20 6L9 17l-5-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            case 'CAUTION': return '⚠️';
            case 'DANGER': return '🚫';
            default: return '❓';
        }
    }

    getRecommendationIcon(rec) {
        switch (rec) {
            case 'FAVORABLE': return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" style="display: inline-block; vertical-align: middle;"><path d="M20 6L9 17l-5-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            case 'CAUTION': return '⚠️';
            case 'DANGER': return '🚫';
            default: return '❓';
        }
    }

    /**
     * Gestión del checklist state
     */
    loadChecklistState() {
        try {
            const accountId = window.currentAccount;
            const today = new Date().toISOString().split('T')[0];
            const key = `TC_CHECKLIST_${accountId}_${today}`;
            const stored = localStorage.getItem(key);
            
            if (stored) {
                const data = JSON.parse(stored);
                this.checklistState = data.items || {};
            } else {
                this.checklistState = {};
            }
        } catch (e) {

            this.checklistState = {};
        }
    }

    saveChecklistState() {
        try {
            const accountId = window.currentAccount;
            const today = new Date().toISOString().split('T')[0];
            const key = `TC_CHECKLIST_${accountId}_${today}`;
            
            const data = {
                date: today,
                timestamp: Date.now(),
                items: this.checklistState,
                completed: this.canStartTrading()
            };
            
            localStorage.setItem(key, JSON.stringify(data));
        } catch (e) {

        }
    }

    /**
     * Toast notifications
     */
    showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `tc-toast ${type}`;
        toast.textContent = message;
        document.body.appendChild(toast);

        setTimeout(() => toast.classList.add('show'), 100);
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    }

    /**
     * Inicia auto-refresh para modo IN-SESSION
     */
    startAutoRefresh() {
        // Actualizar cada 30 segundos
        this.refreshInterval = setInterval(async () => {
            if (this.currentMode === TC_MODES.IN_SESSION && this.isOpen) {
                await window.tradeControl.updateTodayStats();
                this.render();
            }
        }, 30000); // 30 segundos
    }

    /**
     * Refresca manualmente la sesión
     */
    async refreshSession() {
        await window.tradeControl.updateTodayStats();
        await this.updateSessionPanel();
        this.showToast('Datos actualizados', 'success');
    }

    /**
     * Cierra el panel de sesión
     */
    async closeSession() {
        try {
            await window.tradeControl.deactivateAutoManagementSession();
        } catch (error) {
            this.showToast('No se pudo desactivar la sesión en MT.', 'error');
        }

        if (window.planManagerV2 && window.currentAccount) {
            window.planManagerV2.setPlanState(window.currentAccount, 'created');
            window.planManagerV2.updateButtonLabel();
        }

        if (this.sessionPanel) {
            this.sessionPanel.classList.remove('show');
            // Ocultar completamente después de la animación
            setTimeout(() => {
                if (this.sessionPanel) {
                    this.sessionPanel.style.display = 'none';
                }
            }, 300);
        }
        if (this.sessionUpdateInterval) {
            clearInterval(this.sessionUpdateInterval);
        }
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
        this.showToast('Sesión finalizada', 'info');
    }

    /**
     * Setup de event listeners globales
     */
    setupEventListeners() {
        // Escape key para cerrar
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        const resetPlanStateOnClose = () => {
            if (window.planManagerV2 && window.currentAccount) {
                window.planManagerV2.setPlanState(window.currentAccount, 'created');
                window.planManagerV2.updateButtonLabel();
            }
        };

        window.addEventListener('beforeunload', resetPlanStateOnClose);
        window.addEventListener('pagehide', resetPlanStateOnClose);
    }
}

// Inicializar globalmente
if (typeof window !== 'undefined') {
    window.tradeControlUI = new TradeControlUI();
}
